/**
 * Moments: the parts of the studio screen that are plain logic, kept here so
 * they can be tested without rendering MUI.
 *
 * THEMES and the token list MUST match the backend (moments.py) and the mobile
 * app (src/utils/moments.js there). A theme the app does not know falls back to
 * "shino", so a mismatch looks wrong rather than crashing -- but it looks wrong.
 */

export const THEMES = {
    shino: { name: 'Shino', bg: 'linear-gradient(160deg,#FF8F1F,#ED1E79)', card: '#FFFFFF', ink: '#1A1A1A', cta: 'linear-gradient(90deg,#FF8F1F,#ED1E79)', ctaInk: '#FFFFFF', pts: '#FDEAF2', ptsInk: '#ED1E79' },
    midnight: { name: 'Midnight', bg: 'linear-gradient(160deg,#2B1B4E,#60497A)', card: '#1D1530', ink: '#F4EEFF', cta: '#F5C451', ctaInk: '#241A06', pts: '#2F2446', ptsInk: '#F5C451' },
    mint: { name: 'Mint', bg: 'linear-gradient(160deg,#16A085,#6DD5C0)', card: '#FFFFFF', ink: '#0F2E28', cta: '#0F766E', ctaInk: '#FFFFFF', pts: '#E0F5F0', ptsInk: '#0F766E' },
    sunset: { name: 'Festive', bg: 'linear-gradient(160deg,#B91C1C,#F59E0B)', card: '#FFF8EC', ink: '#3B1203', cta: '#B91C1C', ctaInk: '#FFFFFF', pts: '#FDE7C7', ptsInk: '#B91C1C' },
};

export const TOKENS = ['first_name', 'orders_count', 'plan_name', 'points', 'bonus', 'hub'];

export const CTA_TARGETS = [
    { value: 'LoyaltyCashWallet', label: 'Loyalty wallet' },
    { value: 'BonusWallet', label: 'Wallet bonus' },
    { value: 'SubscriptionScreen', label: 'Subscription plans' },
    { value: 'ManageSubscriptionsPage', label: 'My subscriptions' },
    { value: 'Landingpage', label: 'Home' },
    { value: 'ProductDescription', label: 'A product' },
    { value: 'Notifications', label: 'Notifications' },
    { value: '', label: 'Just close' },
];

export const TEMPLATES = [
    {
        id: 'recurring', name: 'Thank a regular', desc: 'Recurring buyers, bonus points', theme: 'shino',
        segments: ['Active Recurring User', 'Repeat User'], audience: 'default',
        headline: "{first_name}, you're a Shino regular!",
        message: '{orders_count} orders and counting. Here are {points} bonus points on us: use them on your next meal.',
        cta_text: 'Claim my points', cta_target: 'LoyaltyCashWallet', reward_points: 200,
    },
    {
        id: 'member', name: 'Member treat', desc: 'Active subscribers, personal', theme: 'midnight',
        segments: [], audience: 'subscribers',
        headline: 'A {plan_name} treat for you',
        message: 'Thanks for being a {plan_name} member, {first_name}. {points} extra points are waiting in your wallet.',
        cta_text: 'See my wallet', cta_target: 'LoyaltyCashWallet', reward_points: 300,
    },
    {
        id: 'winback', name: 'Win back', desc: 'Churned customers, comeback offer', theme: 'sunset',
        segments: ['Churned User'], audience: 'default',
        headline: 'We miss you, {first_name}',
        message: "It's been a while. Come back this week and we'll add {points} points to your wallet.",
        cta_text: 'Order now', cta_target: 'Landingpage', reward_points: 150,
    },
    {
        id: 'upgrade', name: 'Upgrade nudge', desc: 'Past subscribers, no points', theme: 'mint',
        segments: [], audience: 'expired_subscribers',
        headline: 'Your {plan_name} perks are one tap away',
        message: 'Renew today and get free delivery plus 1 point for every ₹1 on every order at {hub}.',
        cta_text: 'Renew plan', cta_target: 'SubscriptionScreen', reward_points: 0,
    },
    {
        id: 'custom', name: 'Start blank', desc: 'Your own words', theme: 'shino',
        segments: [], audience: 'default',
        headline: '', message: '', cta_text: 'Open', cta_target: 'Landingpage', reward_points: 0,
    },
];

export const SAMPLE_PERSON = { first_name: 'Arjun', orders_count: 12, plan_name: 'Gold', hub: 'OMR' };

/** Fill {tokens}; unknown ones stay as written, like the backend. */
export function renderTokens(text, ctx) {
    const out = String(text || '').replace(/\{(\w+)\}/g, (whole, key) => {
        const value = ctx ? ctx[key] : undefined;
        return value === undefined || value === null || value === '' ? whole : String(value);
    });
    if (String(text || '').startsWith('{') && /^[a-z]/.test(out)) {
        return out.charAt(0).toUpperCase() + out.slice(1);
    }
    return out;
}

export function unknownTokens(...texts) {
    const found = new Set();
    texts.forEach((t) => {
        String(t || '').replace(/\{(\w+)\}/g, (_, key) => {
            if (!TOKENS.includes(key)) found.add(key);
            return _;
        });
    });
    return [...found].sort();
}

export function blankMoment() {
    return fromTemplate(TEMPLATES[0], {});
}

/** Apply a template's words, look and audience; keep delivery choices. */
export function fromTemplate(tpl, current) {
    return {
        name: '',
        image_url: '',
        layout: 'card',
        inventory_ids: [],
        subscription_plan_ids: [],
        reward_expiry_days: 30,
        reward_on: 'tap',
        send_push: true,
        send_popup: true,
        send_inbox: true,
        quiet_hours: true,
        scheduled_at: '',
        popup_until: '',
        cta_value: '',
        reward_plan_id: null,
        reward_plan_days: null,
        audience_mode: 'filters',
        user_ids: [],
        excluded_user_ids: [],
        ...current,
        template: tpl.id,
        theme: tpl.theme,
        headline: tpl.headline,
        message: tpl.message,
        cta_text: tpl.cta_text,
        cta_target: tpl.cta_target,
        reward_points: tpl.reward_points,
        reward_bonus_amount: tpl.reward_bonus_amount || 0,
        segments: tpl.segments,
        audience: tpl.audience,
    };
}

/** What the admin can fix before asking the server. */
export function localErrors(m) {
    const errors = {};
    if (!String(m.headline || '').trim()) errors.headline = 'Write a headline.';
    if (!String(m.message || '').trim()) errors.message = 'Write a message.';
    const bad = unknownTokens(m.headline, m.message, m.cta_text);
    if (bad.length) errors.message = `Unknown tag ${bad.map((b) => `{${b}}`).join(', ')}.`;
    const pts = Number(m.reward_points);
    if (!Number.isInteger(pts) || pts < 0) errors.reward_points = 'Points must be a whole number, 0 or more.';
    else if (pts > 5000) errors.reward_points = 'At most 5,000 points per customer.';
    const bonus = Number(m.reward_bonus_amount || 0);
    if (Number.isNaN(bonus) || bonus < 0) errors.reward_bonus_amount = 'Wallet bonus must be ₹0 or more.';
    else if (bonus > 5000) errors.reward_bonus_amount = 'At most ₹5,000 per customer.';
    else if (Math.round(bonus * 100) !== bonus * 100) errors.reward_bonus_amount = 'Use at most 2 decimals.';
    if (m.cta_target === 'ProductDescription' && !/^\d+$/.test(String(m.cta_value || ''))) {
        errors.cta_value = 'Choose the product.';
    }
    if (!m.send_push && !m.send_popup && !m.send_inbox) errors.send_push = 'Pick at least one way to deliver it.';
    if (m.audience_mode === 'pick' && !(m.user_ids || []).length) errors.user_ids = 'Pick at least one customer.';
    return errors;
}

/** A `datetime-local` value ("2026-09-29T18:30") is sent as India time; empty is null. */
export function payload(m) {
    return {
        ...m,
        reward_points: Number(m.reward_points) || 0,
        reward_bonus_amount: Number(m.reward_bonus_amount) || 0,
        reward_plan_id: m.reward_plan_id || null,
        reward_plan_days: m.reward_plan_id && m.reward_plan_days ? Number(m.reward_plan_days) : null,
        reward_expiry_days: Number(m.reward_expiry_days) || 30,
        scheduled_at: m.scheduled_at || null,
        popup_until: m.popup_until || null,
    };
}

/** Server ISO -> `datetime-local` in India time, for editing a saved moment. */
export function toLocalInput(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const ist = new Date(d.getTime() + 330 * 60000);
    return ist.toISOString().slice(0, 16);
}

export function fromServer(row) {
    return {
        ...row,
        scheduled_at: toLocalInput(row.scheduled_at),
        popup_until: toLocalInput(row.popup_until),
    };
}

export function rate(part, whole) {
    if (!whole) return '0%';
    return `${Math.round((part * 100) / whole)}%`;
}

/** Add the id if absent, remove it if present. */
export function toggleId(list, id) {
    const ids = list || [];
    return ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
}

/** Body for the customer list: the audience fields, a search and a page. */
export function customerQuery(m, search, offset, limit) {
    return {
        audience_mode: m.audience_mode || 'filters',
        inventory_ids: m.inventory_ids || [],
        segments: m.segments || [],
        audience: m.audience || 'default',
        subscription_plan_ids: m.subscription_plan_ids || [],
        // So each row can say what the free plan would do for that customer.
        reward_plan_id: m.reward_plan_id || null,
        search: String(search || '').trim(),
        offset,
        limit,
    };
}

/** ₹50 for 50, ₹49.50 for 49.5 -- same as the backend's rupees(). */
export function rupees(amount) {
    const n = Number(amount) || 0;
    return Number.isInteger(n) ? `₹${n}` : `₹${n.toFixed(2)}`;
}

/** What a customer gets, in words: "200 points + ₹50 wallet bonus". */
export function rewardText(points, bonus) {
    const parts = [];
    if (Number(points) > 0) parts.push(`${Number(points)} points`);
    if (Number(bonus) > 0) parts.push(`${rupees(bonus)} wallet bonus`);
    return parts.join(' + ');
}

/** Token values for the preview phone. */
export function tokenContext(person, m) {
    return { ...person, points: Number(m.reward_points) || 0, bonus: rupees(m.reward_bonus_amount) };
}

/** "{first_name}, you're a regular" -> "[first name], you're a regular" for admin lists. */
export function readableTitle(text) {
    return String(text || '').replace(/\{(\w+)\}/g, (_, key) => `[${key.replace(/_/g, ' ')}]`);
}

/**
 * The list's name for a moment: the server's title (a customer's name for one,
 * general words for many), else the raw name with its tags shown as [tags].
 */
export function momentTitle(row) {
    return row?.title || readableTitle(row?.name);
}

/** Admin-facing status names. */
export const STATUS_LABELS = {
    draft: 'Draft',
    scheduled: 'Scheduled',
    delivering: 'Delivering',
    live: 'Live',
    ended: 'Ended',
    cancelled: 'Cancelled',
};

/** What each status allows. Must agree with the backend's rules. */
export function actionsFor(status) {
    switch (status) {
    case 'draft':
    case 'scheduled':
        return ['edit', 'duplicate', 'delete'];
    case 'delivering':
        return ['duplicate'];
    case 'live':
        return ['edit', 'duplicate', 'stop', 'delete'];
    default:
        return ['duplicate', 'delete'];
    }
}

/** Everything a moment gives, for summaries: "200 points + ₹50 wallet bonus + Diamond". */
export function fullRewardText(m, planName) {
    return [rewardText(m.reward_points, m.reward_bonus_amount), m.reward_plan_id ? (planName || 'Free plan') : '']
        .filter(Boolean).join(' + ');
}

/** Free-plan lengths the admin can pick. null = the plan's own length. */
export const PLAN_LENGTHS = [
    { value: null, label: "The plan's own length" },
    { value: 7, label: '7 days' },
    { value: 14, label: '14 days' },
    { value: 30, label: '1 month' },
    { value: 90, label: '3 months' },
];

/* ------------------------------------------------------------ hub checks */

/**
 * The hubs this moment reaches. The preview knows exactly (the hubs of the
 * customers it resolved); before it answers, fall back to the chosen hubs, or
 * every hub when none is chosen. Picked customers have no hub list until the
 * preview answers.
 */
export function audienceHubIds(m, preview, hubs) {
    if (preview && Array.isArray(preview.audience_hub_ids)) return preview.audience_hub_ids;
    if (m.audience_mode === 'pick') return [];
    if ((m.inventory_ids || []).length) return m.inventory_ids;
    return (hubs || []).map((h) => h.id);
}

/** 'all' | 'some' | 'none' | 'unknown': is this plan sold where the audience is? */
export function planCoverage(plan, hubIds) {
    if (!plan) return 'unknown';
    if (plan.all_hubs) return 'all';
    if (!hubIds || !hubIds.length) return 'unknown';
    const sold = hubIds.filter((h) => (plan.hub_ids || []).includes(h));
    if (!sold.length) return 'none';
    return sold.length === hubIds.length ? 'all' : 'some';
}

/** Label for the plan picker: "Diamond · 1 month · sold at 2 of 3 of your hubs". */
export function planOptionLabel(plan, hubIds) {
    const cover = planCoverage(plan, hubIds);
    const where = {
        all: 'all your hubs',
        some: `${(hubIds || []).filter((h) => (plan.hub_ids || []).includes(h)).length} of ${(hubIds || []).length} of your hubs`,
        none: 'not sold at your hubs',
        unknown: plan.all_hubs ? 'all hubs' : `${(plan.hub_ids || []).length} hub${(plan.hub_ids || []).length === 1 ? '' : 's'}`,
    }[cover];
    return `${plan.name} · ${plan.length} · ${where}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "24 Oct". Fixed month names: the en-IN locale writes "Sept" on some systems. */
const shortDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
};

/** "Diamond until 24 Oct", "Gold, ended 12 Sep", or "No plan". */
export function currentPlanText(plan) {
    if (!plan || !plan.name) return 'No plan';
    return plan.active ? `${plan.name} until ${shortDate(plan.until)}` : `${plan.name}, ended ${shortDate(plan.until)}`;
}

/** What the free plan would do for one customer, as a chip: [text, tone]. */
export function outcomeChip(outcome) {
    if (!outcome) return null;
    switch (outcome.status) {
    case 'activated': return ['Starts now', 'ok'];
    case 'extended': return ['Will extend', 'ok'];
    case 'queued': return [`Waits until ${shortDate(outcome.starts)}`, 'warn'];
    case 'not_covered': return ['Plan not sold at their hub', 'bad'];
    default: return null;
    }
}

/** The preview's plan block as rows for "Who gets what". */
export function planBreakdown(plan) {
    if (!plan) return [];
    const rows = [
        ['Plan starts when they claim', plan.activate, 'No plan today'],
    ];
    if (plan.extend) rows.push(['Plan extended', plan.extend, `Already on ${plan.name}; the free time is added on`]);
    if (plan.queue) rows.push(['Plan waits, then starts', plan.queue, `On ${(plan.queued_behind || []).join(', ') || 'another plan'}; free ${plan.name} starts when it ends`]);
    if (plan.not_offered) rows.push(['Moment without the plan', plan.not_offered, `Their hub doesn't sell ${plan.name}. They still get the points or bonus`]);
    if (plan.left_out) rows.push(['Left out', plan.left_out, `Their hub doesn't sell ${plan.name}, and the plan is the only reward`]);
    return rows;
}

export const STOCK_LABELS = { in_stock: 'in stock', out_of_stock: 'out of stock', not_sold: 'not sold' };
